/**
 * Ce que le site a besoin de savoir d'une note partagée par lien (#361).
 *
 * Le corps déposé dans `shared_notes` est le Markdown **brut** de la note, ses
 * références encore entre doubles parenthèses : c'est ce qui permet à
 * l'application destinataire de la retrouver fidèle. Le site, lui, doit en
 * faire une page lisible.
 *
 * Fonctions pures, sans Firestore ni React — donc testables telles quelles.
 */
import { buildReferencePath } from './url-builder';

/**
 * L'alphabet base58 de `functions/src/sharing/share-id.ts`, à l'identique.
 *
 * Contrôler la forme avant d'interroger Firestore évite d'envoyer une lecture
 * pour n'importe quel chemin visité par un robot.
 */
const SHARE_ID = /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{20,24}$/;

export function isValidShareId(value: string | undefined): value is string {
  return typeof value === 'string' && SHARE_ID.test(value);
}

/**
 * Transforme les `((Jn 3, 16))` en liens vers le passage sur bible4.me.
 *
 * Les doubles parenthèses sont la notation de l'éditeur ; laissées telles
 * quelles elles se liraient comme une coquille. Le chemin est construit par
 * [buildReferencePath], celui-là même dont le commentaire du jour se sert.
 *
 * `translation` est l'identifiant de Bible enregistré avec la note : le lien
 * ouvre donc le passage dans la version où il a été cité, et non dans celle du
 * lecteur.
 */
export function linkifyReferences(
  markdown: string,
  lang: string,
  translation?: string | null,
): string {
  if (!markdown) return '';
  return markdown.replace(/\(\(([^()\n]+)\)\)/g, (_match, reference: string) => {
    const path = buildReferencePath(reference);
    if (!path) return reference;
    const query = translation ? `?t=${encodeURIComponent(translation)}` : '';
    // Les crochets d'un libellé Markdown : une référence n'en contient pas,
    // mais une note mal formée ne doit pas casser le rendu de la page.
    const label = reference.trim().replace(/[[\]]/g, '');
    return `[${label}](/${lang}/${path}${query})`;
  });
}

/**
 * Le nom court d'une Bible, à partir de son identifiant.
 *
 * Le site n'embarque pas le registre des traductions de l'application ; il n'a
 * que la clé enregistrée avec la note. La montrer en capitales — « BDP » — dit
 * l'essentiel sans prétendre à un nom complet qu'on n'a pas.
 */
export function translationLabel(id?: string | null): string | null {
  if (!id) return null;
  const trimmed = id.trim();
  return trimmed.length === 0 ? null : trimmed.toUpperCase();
}
